// 座標轉換（plan/vms-web-v0.md §5.2.2）：資料一律存 WGS-84，顯示時轉。
//   - 百度：BD-09（中國大陸先 WGS→GCJ-02 再加百度偏移；大陸以外百度同樣用 BD-09 偏移，但不做 GCJ）
//   - Google：中國大陸範圍內底圖是 GCJ-02 偏移，轉 GCJ-02；台灣、香港、澳門與其他地區用 WGS-84
//   - 反轉換（使用者在地圖上點選）用迭代，回原座標誤差 < 1 cm
// 大陸範圍：外框再挖掉台灣（含澎湖、金門、馬祖）、香港、澳門；邊界附近以多邊形近似（深圳河一帶約 ±300 m）。

export interface LatLng {
  lat: number;
  lng: number;
}

const A = 6378245.0; // Krasovsky 1940
const EE = 0.00669342162296594323;
const X_PI = (Math.PI * 3000.0) / 180.0;

type Ring = [number, number][]; // [lng, lat]

const HONG_KONG: Ring = [
  [113.82, 22.13], [113.82, 22.4], [113.9, 22.47], [113.97, 22.5], [114.03, 22.505], [114.07, 22.51], [114.1, 22.527],
  [114.16, 22.535], [114.22, 22.548], [114.3, 22.56], [114.45, 22.56], [114.45, 22.13],
];
const MACAU: Ring = [[113.52, 22.1], [113.52, 22.215], [113.555, 22.217], [113.6, 22.2], [113.6, 22.1]];
const TAIWAN: Ring = [
  [119.25, 23.1], [119.25, 23.85], [120.4, 24.5], [120.85, 25.1], [121.5, 25.4], [122.2, 25.4], [122.2, 21.8], [120.6, 21.8], [119.9, 22.8],
];
const KINMEN: Ring = [[118.13, 24.36], [118.13, 24.55], [118.5, 24.55], [118.5, 24.36]];
const MATSU: Ring = [[119.85, 25.9], [119.85, 26.4], [120.55, 26.4], [120.55, 25.9]];

function inRing(ring: Ring, lng: number, lat: number): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** 在中國大陸（GCJ-02 生效的範圍）內嗎。 */
export function inMainlandChina(p: LatLng): boolean {
  const { lat, lng } = p;
  if (lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271) return false;
  return ![HONG_KONG, MACAU, TAIWAN, KINMEN, MATSU].some((r) => inRing(r, lng, lat));
}

function tLat(x: number, y: number): number {
  let r = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
  r += ((20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0) / 3.0;
  r += ((20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin((y / 3.0) * Math.PI)) * 2.0) / 3.0;
  r += ((160.0 * Math.sin((y / 12.0) * Math.PI) + 320 * Math.sin((y * Math.PI) / 30.0)) * 2.0) / 3.0;
  return r;
}

function tLng(x: number, y: number): number {
  let r = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
  r += ((20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0) / 3.0;
  r += ((20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin((x / 3.0) * Math.PI)) * 2.0) / 3.0;
  r += ((150.0 * Math.sin((x / 12.0) * Math.PI) + 300.0 * Math.sin((x / 30.0) * Math.PI)) * 2.0) / 3.0;
  return r;
}

/** GCJ-02 偏移（不判斷範圍）。 */
function gcjOffset(p: LatLng): LatLng {
  let dLat = tLat(p.lng - 105.0, p.lat - 35.0);
  let dLng = tLng(p.lng - 105.0, p.lat - 35.0);
  const radLat = (p.lat / 180.0) * Math.PI;
  let magic = Math.sin(radLat);
  magic = 1 - EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  dLat = (dLat * 180.0) / (((A * (1 - EE)) / (magic * sqrtMagic)) * Math.PI);
  dLng = (dLng * 180.0) / ((A / sqrtMagic) * Math.cos(radLat) * Math.PI);
  return { lat: p.lat + dLat, lng: p.lng + dLng };
}

export function wgsToGcj(p: LatLng): LatLng {
  return inMainlandChina(p) ? gcjOffset(p) : { ...p };
}

/** 迭代反解：找 w 使 forward(w) = target。 */
function invert(target: LatLng, forward: (p: LatLng) => LatLng): LatLng {
  let w = { ...target };
  for (let i = 0; i < 30; i++) {
    const f = forward(w);
    const dLat = f.lat - target.lat;
    const dLng = f.lng - target.lng;
    w = { lat: w.lat - dLat, lng: w.lng - dLng };
    if (Math.abs(dLat) < 1e-10 && Math.abs(dLng) < 1e-10) break;
  }
  return w;
}

export function gcjToWgs(p: LatLng): LatLng {
  return invert(p, wgsToGcj);
}

/** GCJ-02 → BD-09（百度偏移）。 */
export function gcjToBd(p: LatLng): LatLng {
  const x = p.lng;
  const y = p.lat;
  const z = Math.sqrt(x * x + y * y) + 0.00002 * Math.sin(y * X_PI);
  const theta = Math.atan2(y, x) + 0.000003 * Math.cos(x * X_PI);
  return { lat: z * Math.sin(theta) + 0.006, lng: z * Math.cos(theta) + 0.0065 };
}

export function bdToGcj(p: LatLng): LatLng {
  return invert(p, gcjToBd);
}

export function wgsToBd(p: LatLng): LatLng {
  return gcjToBd(wgsToGcj(p));
}

export function bdToWgs(p: LatLng): LatLng {
  return invert(p, wgsToBd);
}

export type MapDatum = 'wgs84' | 'gcj02' | 'bd09';

/** 資料（WGS-84）→ 某家地圖要的座標。 */
export function toDisplay(p: LatLng, provider: 'google' | 'baidu'): LatLng {
  return provider === 'baidu' ? wgsToBd(p) : wgsToGcj(p);
}

/** 地圖上點到的座標 → 資料（WGS-84）。 */
export function fromDisplay(p: LatLng, provider: 'google' | 'baidu'): LatLng {
  return provider === 'baidu' ? bdToWgs(p) : gcjToWgs(p);
}

/** 兩點大圓距離（公尺，haversine）。 */
export function distanceM(a: LatLng, b: LatLng): number {
  const R = 6371008.8;
  const r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r;
  const dLng = (b.lng - a.lng) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** 從 p 往 bearing（度，0 = 北、順時針）走 dist 公尺。畫攝影機視角扇形用。 */
export function destination(p: LatLng, bearingDeg: number, distM: number): LatLng {
  const R = 6371008.8;
  const r = Math.PI / 180;
  const d = distM / R;
  const b = bearingDeg * r;
  const lat1 = p.lat * r;
  const lng1 = p.lng * r;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(b));
  const lng2 = lng1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return { lat: lat2 / r, lng: lng2 / r };
}

/** 攝影機視角扇形（WGS-84 多邊形）。 */
export function sector(p: LatLng, headingDeg: number, fovDeg: number, rangeM: number, steps = 12): LatLng[] {
  const out: LatLng[] = [p];
  const start = headingDeg - fovDeg / 2;
  for (let i = 0; i <= steps; i++) out.push(destination(p, start + (fovDeg * i) / steps, rangeM));
  return out;
}

// ---- 平面圖對齊（FP-02）：控制點 → 仿射矩陣 ----
// 矩陣 [a, b, c, d, e, f]：lng = a·x + b·y + e、lat = c·x + d·y + f（x、y 是平面圖的正規化座標 0..1）。
// 在局部範圍（一棟建築）把經緯度當平面座標夠準（誤差遠小於 1 m）。

export interface ControlPoint {
  x: number;
  y: number;
  lat: number;
  lng: number;
}

export type Affine = [number, number, number, number, number, number];

/** 2 點 → 相似轉換（平移、旋轉、等比縮放；考慮經度隨緯度縮短）；3 點 → 仿射。 */
export function solveAffine(pts: ControlPoint[], aspect = 1): Affine | null {
  if (pts.length === 2) {
    // 在公尺平面上做相似轉換，再換回經緯度
    const [p, q] = pts;
    const k = Math.cos((((p.lat + q.lat) / 2) * Math.PI) / 180);
    // 圖面座標（x 乘寬高比，讓兩軸同單位）
    const u1 = { x: p.x * aspect, y: p.y };
    const u2 = { x: q.x * aspect, y: q.y };
    // 目標平面（東、北，經度乘 cos(lat)）
    const v1 = { x: p.lng * k, y: p.lat };
    const v2 = { x: q.lng * k, y: q.lat };
    const du = { x: u2.x - u1.x, y: u2.y - u1.y };
    const dv = { x: v2.x - v1.x, y: v2.y - v1.y };
    const den = du.x * du.x + du.y * du.y;
    if (den === 0) return null;
    // 圖面 y 往下，地圖北往上：先把圖面 y 反向
    const duf = { x: du.x, y: -du.y };
    const s = { re: (dv.x * duf.x + dv.y * duf.y) / den, im: (dv.y * duf.x - dv.x * duf.y) / den };
    // v = s * (x*aspect, -y) + t
    const t = { x: v1.x - (s.re * u1.x - s.im * -u1.y), y: v1.y - (s.im * u1.x + s.re * -u1.y) };
    // 換回：lng = (s.re*aspect*x + s.im*y + t.x)/k ; lat = s.im*aspect*x - s.re*y + t.y
    return [(s.re * aspect) / k, s.im / k, s.im * aspect, -s.re, t.x / k, t.y];
  }
  if (pts.length === 3) {
    const [p1, p2, p3] = pts;
    const det = p1.x * (p2.y - p3.y) - p2.x * (p1.y - p3.y) + p3.x * (p1.y - p2.y);
    if (Math.abs(det) < 1e-12) return null;
    const solve = (v1: number, v2: number, v3: number): [number, number, number] => [
      (v1 * (p2.y - p3.y) - v2 * (p1.y - p3.y) + v3 * (p1.y - p2.y)) / det,
      (p1.x * (v2 - v3) - p2.x * (v1 - v3) + p3.x * (v1 - v2)) / det,
      (p1.x * (p2.y * v3 - p3.y * v2) - p2.x * (p1.y * v3 - p3.y * v1) + p3.x * (p1.y * v2 - p2.y * v1)) / det,
    ];
    const [a, b, e] = solve(p1.lng, p2.lng, p3.lng);
    const [c, d, f] = solve(p1.lat, p2.lat, p3.lat);
    return [a, b, c, d, e, f];
  }
  return null;
}

export function applyAffine(m: Affine, x: number, y: number): LatLng {
  return { lng: m[0] * x + m[1] * y + m[4], lat: m[2] * x + m[3] * y + m[5] };
}

/** 反向：經緯度 → 平面圖正規化座標。 */
export function invertAffine(m: Affine, p: LatLng): { x: number; y: number } | null {
  const [a, b, c, d, e, f] = m;
  const det = a * d - b * c;
  if (Math.abs(det) < 1e-18) return null;
  const u = p.lng - e;
  const v = p.lat - f;
  return { x: (d * u - b * v) / det, y: (-c * u + a * v) / det };
}
